import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CureSheetsService, refusalMessage } from '../services/cure-sheets.service';
import { EffectiveCureSheet } from '../services/cure-sheets.types';

/**
 * Compact panel of cure-sheet download links for end users. The default sheet
 * is badged and listed first (server orders it first). Renders nothing when
 * the company has no sheets enabled.
 */
@Component({
  selector: 'app-cure-sheet-list',
  templateUrl: './cure-sheet-list.component.html',
  styleUrls: ['./cure-sheet-list.component.scss'],
})
export class CureSheetListComponent implements OnInit, OnDestroy {
  sheets: EffectiveCureSheet[] = [];
  defaultSheetId: string | null = null;

  private readonly _destroying$ = new Subject<void>();

  constructor(private cureSheets: CureSheetsService) {}

  ngOnInit(): void {
    this.cureSheets.effective$
      .pipe(takeUntil(this._destroying$))
      .subscribe((response) => {
        this.sheets = response?.sheets ?? [];
        this.defaultSheetId = response?.defaultSheetId ?? null;
      });
    this.cureSheets.getEffectiveSheets().subscribe();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  isDefault(sheet: EffectiveCureSheet): boolean {
    return sheet.id === this.defaultSheetId;
  }

  // Fetched through the API, not linked to the stored blob URL — that URL is a
  // ten-year SAS which keeps working after a version's approval is withdrawn, and
  // a plain <a href> could not carry the bearer anyway (the auth interceptor only
  // sees HttpClient traffic).

  /** "<sheetId>@<version>" while its PDF is being fetched. */
  pdfPending: string | null = null;
  pdfError = '';

  pdfKey(sheet: EffectiveCureSheet): string {
    return `${sheet.id}@${sheet.version}`;
  }

  openPdf(sheet: EffectiveCureSheet): void {
    if (this.pdfPending) return;

    this.pdfPending = this.pdfKey(sheet);
    this.pdfError = '';

    this.cureSheets.getVersionPdf(sheet.id, sheet.version).subscribe({
      next: (blob) => {
        this.pdfPending = null;
        const url = URL.createObjectURL(blob);
        if (!window.open(url, '_blank')) {
          this.pdfError = 'Blocked by a popup blocker — allow popups for this site.';
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      },
      error: (err: unknown) => {
        this.pdfPending = null;
        this.pdfError = refusalMessage(err, 'Could not open that cure sheet.');
      },
    });
  }
}
